import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, AlertCircle, Minus, Plus } from 'lucide-react';
import {
  Product,
  SelectedOption,
  OrderItemCustomizations,
  validateAllSelections,
  calculateTotalPriceModifier,
  calculateCustomizedPrice,
  applyDefaultOptions,
} from '../types';
import OptionGroupSelector from './OptionGroupSelector';
import { useMenuTheme } from '@/contexts/MenuThemeContext';

interface ProductCustomizationModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: Product, customizations: OrderItemCustomizations, quantity: number) => void;
  language?: string;
}

export default function ProductCustomizationModal({
  product,
  isOpen,
  onClose,
  onAddToCart,
  language = 'it',
}: ProductCustomizationModalProps) {
  const { theme } = useMenuTheme();
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const [freeTextNotes, setFreeTextNotes] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (isOpen && product.customizationOptions) {
      const defaults = applyDefaultOptions(product.customizationOptions);
      setSelectedOptions(defaults);
      setFreeTextNotes('');
      setQuantity(1);
      setValidationErrors({});
      setShowErrors(false);
    }
  }, [isOpen, product]);

  const totalPriceModifier = calculateTotalPriceModifier(selectedOptions);
  const basePrice = product.price;
  const pricePerItem = calculateCustomizedPrice(basePrice, {
    selectedOptions,
    freeTextNotes,
    totalPriceModifier,
  });
  const totalPrice = pricePerItem * quantity;

  const validateSelections = () => {
    if (!product.customizationOptions) return true;

    const result = validateAllSelections(product.customizationOptions, selectedOptions);

    if (!result.valid) {
      const errorMap: Record<string, string> = {};
      result.errors.forEach((error) => {
        errorMap[error.field] = error.message;
      });
      setValidationErrors(errorMap);
      setShowErrors(true);
      return false;
    }

    setValidationErrors({});
    setShowErrors(false);
    return true;
  };

  const handleAddToCart = () => {
    if (!validateSelections()) {
      return;
    }

    const customizations: OrderItemCustomizations = {
      selectedOptions,
      freeTextNotes: freeTextNotes.trim() || null,
      totalPriceModifier,
    };

    onAddToCart(product, customizations, quantity);
    onClose();
  };

  const handleOptionChange = (groupId: string, newSelections: SelectedOption[]) => {
    setSelectedOptions(newSelections);
    if (validationErrors[groupId]) {
      const newErrors = { ...validationErrors };
      delete newErrors[groupId];
      setValidationErrors(newErrors);
    }
  };

  if (!product.customizationOptions) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: "100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 top-[10%] sm:top-[5%] sm:inset-x-4 sm:bottom-4 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg rounded-t-3xl sm:rounded-3xl z-50 flex flex-col overflow-hidden"
            style={{
              backgroundColor: theme.cardBackground,
              borderColor: theme.borderColor,
              borderWidth: '1px',
            }}
          >
            {/* Header */}
            <div
              className="relative p-4"
              style={{ borderBottomColor: theme.borderColor, borderBottomWidth: '1px' }}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute right-3 top-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ backgroundColor: `${theme.textColor}0d` }}
              >
                <X size={18} style={{ color: theme.textSecondary }} />
              </button>

              <div className="pr-10">
                <h2 className="text-lg font-bold line-clamp-1" style={{ color: theme.textColor }}>
                  {product.name}
                </h2>
                {product.description && (
                  <p className="text-sm mt-0.5 line-clamp-1" style={{ color: theme.mutedColor }}>
                    {product.description}
                  </p>
                )}
                <span className="font-semibold text-sm mt-1 inline-block" style={{ color: theme.primaryColor }}>
                  €{basePrice.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Option Groups */}
              {product.customizationOptions.optionGroups
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((optionGroup) => (
                  <OptionGroupSelector
                    key={optionGroup.id}
                    optionGroup={optionGroup}
                    selectedOptions={selectedOptions}
                    onChange={(newSelections) =>
                      handleOptionChange(optionGroup.id, newSelections)
                    }
                    language={language}
                    error={showErrors ? validationErrors[optionGroup.id] : undefined}
                    theme={theme}
                  />
                ))}

              {/* Free Text Notes */}
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: theme.textSecondary }}>
                  Note aggiuntive
                </label>
                <textarea
                  value={freeTextNotes}
                  onChange={(e) => setFreeTextNotes(e.target.value)}
                  placeholder="Es: Senza cipolle, ben cotto..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl text-sm resize-none focus:outline-none transition-colors"
                  style={{
                    backgroundColor: `${theme.textColor}0d`,
                    borderColor: theme.borderColor,
                    borderWidth: '1px',
                    color: theme.textColor,
                  }}
                  maxLength={200}
                />
              </div>

              {/* Validation Error */}
              {showErrors && Object.keys(validationErrors).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl"
                >
                  <AlertCircle size={16} className="text-red-400 shrink-0" />
                  <p className="text-sm text-red-400">
                    Completa le selezioni obbligatorie
                  </p>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div
              className="p-4"
              style={{
                backgroundColor: theme.backgroundColor,
                borderTopColor: theme.borderColor,
                borderTopWidth: '1px'
              }}
            >
              {/* Quantity & Price Row */}
              <div className="flex items-center justify-between mb-3">
                {/* Quantity Selector */}
                <div
                  className="flex items-center gap-2 rounded-xl px-2 py-1"
                  style={{ backgroundColor: `${theme.textColor}0d` }}
                >
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                    style={{ backgroundColor: `${theme.textColor}1a` }}
                  >
                    <Minus size={16} style={{ color: theme.textColor }} />
                  </button>
                  <span className="w-8 text-center text-lg font-bold" style={{ color: theme.textColor }}>
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
                    style={{ backgroundColor: `${theme.textColor}1a` }}
                  >
                    <Plus size={16} style={{ color: theme.textColor }} />
                  </button>
                </div>

                {/* Price Summary */}
                <div className="text-right">
                  {totalPriceModifier !== 0 && (
                    <p className="text-xs" style={{ color: theme.mutedColor }}>
                      Base €{basePrice.toFixed(2)} {totalPriceModifier > 0 ? '+' : ''}€{totalPriceModifier.toFixed(2)}
                    </p>
                  )}
                  <p className="text-xl font-bold" style={{ color: theme.primaryColor }}>
                    €{totalPrice.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Add to Cart Button */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleAddToCart}
                className="w-full py-3 text-white font-semibold rounded-xl flex items-center justify-center gap-2 text-sm"
                style={{
                  background: theme.buttonGradient,
                  boxShadow: `0 10px 25px -5px ${theme.shadowColor}`
                }}
              >
                <ShoppingCart size={18} />
                <span>Aggiungi al carrello</span>
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
